/**
 * Client for the eUICC inventory + pin/unpin/manual-evict admin endpoints
 * exposed on the `api` Function (apps/frontend-new/functions/src/routes/provisioning.ts).
 *
 * Permission gating happens server-side via canWriteDeviceFromAuth — this
 * client is dumb HTTP. The UI should still hide pin/unpin/delete controls
 * when the user doesn't have access, but the server is the source of truth.
 */
import { apiRequest, APIError } from "./api";

/**
 * The eUICC list read hits the Pi live via lpac. When the modem is busy or
 * mid-(re)attach the central-server returns a transient 502/503/504 (Pi
 * timeout / temporarily unavailable). These clear within a second or two, so
 * retry a couple of times before surfacing the error instead of dumping the
 * operator into an error banner on first open.
 */
const TRANSIENT_STATUSES = new Set([502, 503, 504]);

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export interface EuiccInstalledProfile {
  iccid: string;
  state?: "enabled" | "disabled" | "evicted" | string;
  profileName?: string | null;
  serviceProviderName?: string | null;
  profileNickname?: string | null;
  isdpAid?: string | null;
  profileClass?: string | null;
  /**
   * Admin-set pin flag, merged into the live lpac inventory by the
   * provisioning route from the `installed_profiles` Firestore
   * subcollection. Pinned profiles are never auto-evicted by the LRU.
   */
  pinned?: boolean;
  /**
   * Computed by `decorateWithPinnedState()` on the server:
   * `esim_profiles.downloadCount >= downloadLimit`. When true the activation
   * code can't reinstall this profile and the LRU refuses to evict it; admin
   * must unpin to allow destructive eviction.
   */
  autoPinned?: boolean;
  /** Unix-ms when the ICCID was first seen by the cloud (server-stamped on first lpac list). */
  installedAt?: number | null;
  /** Unix-ms when the profile was last enabled (proxy for last use). */
  lastUsedAt?: number | null;
  /**
   * Display name from the `esim_profiles` inventory doc matching this ICCID
   * (joined server-side). Preferred over the on-card `profileName`, which is
   * often vendor placeholder text like "Operational Profile Name 1".
   */
  inventoryName?: string | null;
  /** Operator/network name from the matching `esim_profiles` inventory doc. */
  networkName?: string | null;
  /**
   * True when a `shared: true` `esim_profiles` doc matches this ICCID — i.e.
   * this resident SIM has been promoted into the super-admin shared inventory.
   * Drives the "Shared" badge + Mark/Unmark toggle in the eUICC modal.
   */
  shared?: boolean;
}

export interface EuiccCapacityClient {
  eid?: string | null;
  freeNonVolatileMemoryKb?: number | null;
  totalNonVolatileMemoryKb?: number | null;
  installedApplications?: number | null;
  installedProfilesCount?: number | null;
  lastQueriedAt?: string | null;
}

export interface EuiccListResponse {
  installedProfiles?: EuiccInstalledProfile[];
  euiccCapacity?: EuiccCapacityClient | null;
  durationMs?: number | null;
  error?: string;
  /** #247: served from the last persisted card snapshot (backhaul modem). */
  cached?: boolean;
  cachedAt?: string | null;
}

/** #247: a backhaul eSIM switch / inventory-refresh job (esim_provisioning_jobs). */
export interface BackhaulSwitchJob {
  id: string;
  operation: 'backhaul_switch' | 'backhaul_inventory_refresh';
  status: 'pending' | 'dispatched' | 'in_progress' | 'verifying' | 'completed' | 'rolled_back' | 'failed' | 'lost';
  phase?: string | null;
  phases?: Array<{ phase: string; at: number; ok?: boolean | null; detail?: string | null }>;
  outcome?: 'completed' | 'rolled_back' | 'failed' | 'failed_unrecoverable' | null;
  code?: string | null;
  error?: string | null;
  rollbackError?: string | null;
  targetIccid?: string | null;
  targetApn?: string | null;
  apnSource?: string | null;
  prevIccid?: string | null;
  enabledIccid?: string | null;
  dedupOutcome?: string | null;
  verifyWindowS?: number;
  maintenanceUntil?: string | null;
  installedProfiles?: EuiccInstalledProfile[] | null;
  euiccCapacity?: EuiccCapacityClient | null;
  createdAt?: string;
  completedAt?: string | null;
}

export const euiccService = {
  /**
   * Force a fresh `lpac chip info` + `lpac profile list` on the Pi.
   * Retries transient Pi-busy errors (502/503/504) a couple of times with
   * backoff so a modem mid-(re)attach doesn't surface as a hard failure.
   */
  async refreshProfiles(deviceId: string, modemImei: string): Promise<EuiccListResponse> {
    const path = `/provisioning/euicc/${encodeURIComponent(deviceId)}/${encodeURIComponent(modemImei)}/profiles`;
    const maxAttempts = 3;
    let lastErr: unknown;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        return await apiRequest<EuiccListResponse>(path, { method: "GET" });
      } catch (err) {
        lastErr = err;
        const status = err instanceof APIError ? err.statusCode : 0;
        if (!TRANSIENT_STATUSES.has(status) || attempt === maxAttempts - 1) throw err;
        await sleep(1500 * (attempt + 1));
      }
    }
    throw lastErr;
  },

  /**
   * Promote (or demote) a resident SIM into the super-admin shared inventory.
   * Server enforces super-admin-only. `name`/`apn`/`networkName` seed the
   * created `esim_profiles` doc; ignored when `shared: false`.
   */
  async markShared(args: {
    deviceId: string;
    modemImei: string;
    iccid: string;
    shared: boolean;
    name?: string;
    apn?: string;
    networkName?: string;
    serviceProviderName?: string;
    state?: string;
  }): Promise<{ success: boolean; profileId: string; shared: boolean }> {
    return apiRequest<{ success: boolean; profileId: string; shared: boolean }>(
      "/provisioning/euicc/mark-shared",
      { method: "POST", body: JSON.stringify(args) },
    );
  },

  /**
   * #247: start an async eSIM switch (or inventory refresh) on the device's
   * backhaul modem. Returns 202 + jobId; poll `getBackhaulSwitchJob`.
   */
  async startBackhaulSwitch(args: {
    deviceId: string;
    esimProfileId?: string;
    iccid?: string;
    apn?: string;
    verifyWindowS?: number;
    mode?: 'switch' | 'inventory_refresh';
  }): Promise<{ jobId: string; requestId?: string; targetIccid?: string | null; targetApn?: string | null; apnSource?: string | null; dedupOutcome?: string | null }> {
    return apiRequest<any>("/provisioning/euicc/backhaul-switch", {
      method: "POST",
      body: JSON.stringify(args),
    });
  },

  async getBackhaulSwitchJob(jobId: string): Promise<BackhaulSwitchJob> {
    return apiRequest<BackhaulSwitchJob>(`/provisioning/euicc/backhaul-switch/${encodeURIComponent(jobId)}`, { method: "GET" });
  },

  /** Pin (or unpin) an installed profile. Server enforces tenancy. */
  async setPin(args: {
    deviceId: string;
    modemImei: string;
    iccid: string;
    pinned: boolean;
  }): Promise<{ success: boolean }> {
    return apiRequest<{ success: boolean }>("/provisioning/euicc/pin", {
      method: "POST",
      body: JSON.stringify(args),
    });
  },

  /** Permanently delete an installed profile. Pi refuses if it's enabled. */
  async deleteProfile(args: {
    deviceId: string;
    modemImei: string;
    iccid: string;
  }): Promise<EuiccListResponse & { success?: boolean }> {
    return apiRequest<EuiccListResponse & { success?: boolean }>("/provisioning/euicc/delete", {
      method: "POST",
      body: JSON.stringify(args),
    });
  },

  /** Remotely enable an installed (currently-disabled) profile. */
  async enableProfile(args: {
    deviceId: string;
    modemImei: string;
    iccid: string;
  }): Promise<{ success: boolean; installedProfiles?: EuiccInstalledProfile[]; euiccCapacity?: EuiccCapacityClient | null; error?: string }> {
    return apiRequest<any>("/provisioning/euicc/enable", {
      method: "POST",
      body: JSON.stringify(args),
    });
  },

  /**
   * Install a fresh profile from an activation code. Accepts either a full
   * LPA string ("LPA:1$smdp.example.com$ABC123-DEF") or pre-parsed parts.
   */
  async installProfile(args: {
    deviceId: string;
    modemImei: string;
    lpaString?: string;
    smdpAddress?: string;
    matchingId?: string;
    confirmationCode?: string;
  }): Promise<{ success: boolean; iccid?: string; installedProfiles?: EuiccInstalledProfile[]; euiccCapacity?: EuiccCapacityClient | null; error?: string; lpacStderr?: string }> {
    return apiRequest<any>("/provisioning/euicc/install", {
      method: "POST",
      body: JSON.stringify(args),
    });
  },
};
